/**
 * AuthContext
 *
 * Centralized authentication provider managing Firebase auth state, profile
 * fetching/merging, Stripe checkout handoff, enterprise verification, and
 * defensive recovery flows (token refresh, orphaned users, retries).
 */
import React, { createContext, useContext, useEffect, useState } from 'react';
import { auth } from '../../firebase/firebase';
import { 
  onAuthStateChanged, 
  signOut as firebaseSignOut,
  setPersistence, 
  browserLocalPersistence,
  browserSessionPersistence
} from 'firebase/auth';
import axios from 'axios';

// Import auth modules
import { fetchUserProfile, setUserProfile } from './authProfile';
import {
  signIn as authSignIn,
  signUp as authSignUp,
  completeNewSignup,
  signUpWithGoogle as authSignUpWithGoogle,
  signInWithGoogle as authSignInWithGoogle
} from './authAuthentication';
import {
  clearUserSessionData,
  clearAllStoredAuthErrors,
  storeAuthBackup,
  STRIPE_CHECKOUT_STATE_KEY,
  STRIPE_SESSION_ID_KEY
} from './authSessionUtils';
import {
  initiateStripeCheckout as stripeInitiateCheckout,
  finalizeStripeCheckout as stripeFinalizeCheckout,
  isReturningFromStripeCheckout,
  validateStripeAuthState
} from './authStripeHandlers';
import {
  isEnterpriseUser,
  enterpriseNeedsVerification,
  fetchEnterpriseData,
  checkEnterpriseDomain,
  createMinimalEnterpriseUser,
  buildEnterpriseVerificationState
} from './authEnterpriseHandlers';

// Import utilities
import { SERVER_URL, clearTokenCache } from '../../api/core';
import { syncSubscriptionData } from '../../services/subscriptionSyncService';
import { isUserAdmin } from '../../utils/admin/adminUtils';
import { clearAllAuthErrors } from '../../utils/errors/authErrorUtils';
import { detectAndRepairOrphanedUser } from '../../utils/admin/orphanedUserUtils';
import api from '../../api';

const AuthContext = createContext();

// Navigation service reference
const navigationRef = React.createRef();

export function useAuth() {
  return useContext(AuthContext);
}

export function setNavigator(navigate) {
  if (navigate) {
    navigationRef.current = navigate;
    console.log('AuthContext: Navigator set up');
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isProfileLoaded, setIsProfileLoaded] = useState(false);
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [isSigningUp, setIsSigningUp] = useState(false);
  const [newSignup, setNewSignup] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [lastError, setLastError] = useState(null);
  const [checkingStripe, setCheckingStripe] = useState(false);
  const [isStripeCheckoutInProgress, setIsStripeCheckoutInProgress] = useState(false);
  const [directFirebaseUser, setDirectFirebaseUser] = useState(null);

  // Set a flag when user is transitioning so we can disable auth state changes
  useEffect(() => {
    if (isTransitioning) {
      sessionStorage.setItem('isTransitioning', 'true');
    } else {
      sessionStorage.removeItem('isTransitioning');
    }
  }, [isTransitioning]);

  // Periodic cleanup of stale auth errors
  useEffect(() => {
    const cleanupInterval = setInterval(() => {
      const currentPath = window.location.pathname;
      const isOnSignInPage = currentPath === '/login' || currentPath === '/signin';
      const hasStripeSession = localStorage.getItem('stripe_checkout_initiated') === 'true';
      
      if (!user && !isOnSignInPage && !hasStripeSession) {
        console.log('Periodic cleanup: Clearing stale auth errors');
        clearAllAuthErrors();
      }
    }, 30000);
    
    return () => clearInterval(cleanupInterval);
  }, [user]);

  // Listen for token expiration events from API requests
  useEffect(() => {
    const handleTokenExpired = () => {
      console.log('AuthContext: Token expiration event detected');
      clearAllAuthErrors();
      
      if (auth.currentUser) {
        console.log('AuthContext: Attempting to refresh expired token');
        auth.currentUser.getIdToken(true)
          .then(newToken => {
            console.log('AuthContext: Successfully refreshed expired token, length:', newToken?.length);
          })
          .catch(refreshError => {
            console.error('AuthContext: Failed to refresh expired token:', refreshError);
            console.log('AuthContext: Token refresh failed, user needs to re-authenticate');
            
            setUser(null);
            setIsProfileLoaded(false);
            
            const currentPath = window.location.pathname;
            if (currentPath.startsWith('/app') && navigationRef.current) {
              navigationRef.current('/login', { 
                replace: true,
                state: { message: 'Your session has expired. Please sign in again.' }
              });
            }
          });
      }
    };

    window.addEventListener('auth-token-expired', handleTokenExpired);
    return () => window.removeEventListener('auth-token-expired', handleTokenExpired);
  }, [user]);

  // Ensure Firebase auth persistence is set on mount
  useEffect(() => {
    const ensureAuthPersistence = async () => {
      try {
        await setPersistence(auth, browserLocalPersistence);
        console.log('AuthContext: Firebase auth persistence set to LOCAL for maximum reliability');
        
        if (auth.currentUser) {
          try {
            const token = await auth.currentUser.getIdToken(false);
            console.log('AuthContext: Verified auth token on mount, length:', token?.length);
          } catch (tokenError) {
            console.warn('AuthContext: Failed to get token on mount, will retry when needed:', tokenError);
          }
        }
      } catch (error) {
        console.error('AuthContext: Error setting auth persistence:', error);
        try {
          await setPersistence(auth, browserSessionPersistence);
          console.log('AuthContext: Fallback to SESSION persistence');
        } catch (fallbackError) {
          console.error('AuthContext: Failed to set any persistence:', fallbackError);
        }
      }
    };
    ensureAuthPersistence();
  }, []);

  // Cleanup justLoggedIn flag after profile is loaded
  useEffect(() => {
    if (user && isProfileLoaded) {
      const justLoggedIn = sessionStorage.getItem('justLoggedIn') === 'true';
      if (justLoggedIn) {
        console.log('AuthContext: Profile loaded for logged in user, clearing justLoggedIn flag');
        sessionStorage.removeItem('justLoggedIn');
      }
    }
  }, [user, isProfileLoaded]);

  // Main auth state observer
  useEffect(() => {
    if (!auth) {
      console.error('Auth not initialized - waiting for initialization');
      const retryInitCheck = setTimeout(() => {
        setLoading(false);
        window.location.reload();
      }, 2000);
      return () => clearTimeout(retryInitCheck);
    }
    
    console.log('Setting up Firebase auth state observer');
    
    console.log('AuthContext DEBUG: On mount auth state check:', {
      currentUser: auth.currentUser ? `${auth.currentUser.uid}` : 'null',
      authRecoveryData: localStorage.getItem('auth_recovery_data') ? 'present' : 'null',
      authBackup: localStorage.getItem('auth_backup') ? 'present' : 'null',
      justLoggedIn: sessionStorage.getItem('justLoggedIn'),
      googleTimestamp: localStorage.getItem('google_signin_timestamp'),
      authMethod: sessionStorage.getItem('auth_method')
    });
    
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      console.log('AuthContext DEBUG: onAuthStateChanged triggered with user:', firebaseUser ? `${firebaseUser.uid} (${firebaseUser.email})` : 'null');
      
      // Prevent rapid successive auth state changes
      const now = Date.now();
      const lastAuthProcessing = sessionStorage.getItem('last_auth_processing');
      const lastProcessedUid = sessionStorage.getItem('last_processed_uid');
      
      if (lastAuthProcessing && lastProcessedUid) {
        const timeSinceLastAuth = now - parseInt(lastAuthProcessing);
        const currentUid = firebaseUser?.uid;
        
        if (timeSinceLastAuth < 1000 && lastProcessedUid === currentUid && user && isProfileLoaded) {
          console.log(`AuthContext: Skipping duplicate auth processing for user ${currentUid} (${timeSinceLastAuth}ms since last) - profile already loaded`);
          return;
        }
      }
      
      sessionStorage.setItem('last_auth_processing', now.toString());
      sessionStorage.setItem('last_processed_uid', firebaseUser?.uid || 'null');
      
      setIsProfileLoaded(false);
      setDirectFirebaseUser(firebaseUser);
      console.log('AuthContext: Set directFirebaseUser state:', firebaseUser ? `${firebaseUser.uid}` : 'null');
      
      try {
        const currentPath = window.location.pathname;
        const justLoggedIn = sessionStorage.getItem('justLoggedIn') === 'true';
        const justSignedUp = localStorage.getItem('just_signed_up') === 'true';
        const googleSignInInProgress = sessionStorage.getItem('googleSignInInProgress') === 'true';
        const authError = localStorage.getItem('refrral_auth_error');
        
        // Check if returning from Stripe checkout
        const stripeCheckoutInitiated = localStorage.getItem('stripe_checkout_initiated') === 'true';
        const stripeAuthState = localStorage.getItem('stripe_checkout_auth_state');
        const isSuccessOrCancelPage = currentPath === '/success' || currentPath === '/cancel';
        const isComingFromStripe = stripeCheckoutInitiated && (isSuccessOrCancelPage || currentPath === '/pricing');
        
        console.log('AuthContext: Auth state changed, flags:', { 
          transitioning: sessionStorage.getItem('isTransitioning') === 'true',
          justLoggedIn,
          justSignedUp,
          googleSignInInProgress,
          authError: authError ? 'yes' : 'no',
          currentPath,
          isComingFromStripe,
          stripeCheckoutInitiated
        });
        
        // Handle Stripe checkout return
        if (isComingFromStripe) {
          console.log('AuthContext: User returning from Stripe checkout, preserving auth state');
          localStorage.removeItem('stripe_checkout_initiated');
          
          if (stripeAuthState && firebaseUser) {
            validateStripeAuthState(firebaseUser, stripeAuthState);
          }
        }
        
        // Skip if auth error present
        if (authError) {
          console.log('AuthContext: Auth error detected, skipping profile fetch');
          setLoading(false);
          return;
        }
        
        // Skip during Google sign-in
        if (googleSignInInProgress) {
          console.log('AuthContext: Google sign-in in progress, deferring auth state change');
          setLoading(false);
          return;
        }
        
        // Skip during Google sign-up
        const googleSignUpInProgress = sessionStorage.getItem('googleSignUpInProgress') === 'true';
        if (googleSignUpInProgress) {
          console.log('AuthContext: Google sign-up in progress, deferring auth state change');
          setLoading(false);
          return;
        }
        
        // Handle new signups
        const comingFromSignup = sessionStorage.getItem('coming_from_signup') === 'true';
        
        if (justSignedUp || comingFromSignup) {
          console.log('AuthContext: User just signed up, ensuring profile load for new user');
        } else if (isTransitioning) {
          console.log('AuthContext: Auth state transitioning, deferring state change until complete');
          return;
        }

        if (firebaseUser) {
          // Skip if Google sign-in in progress
          if (googleSignInInProgress) {
            console.log("AuthContext: Google sign-in in progress, skipping automatic profile fetch");
            setIsTransitioning(true);
            return;
          }
          
          console.log("AuthContext: Firebase user detected, fetching profile...");
          
          // Skip if profile already loaded for this user
          if (user && user.uid === firebaseUser.uid && isProfileLoaded) {
            console.log("AuthContext: Profile already loaded for this user, skipping duplicate fetch");
            setLoading(false);
            return;
          }
          
          try {
            const updatedUser = await fetchUserProfile(firebaseUser, setUser, true);
            console.log("AuthContext: Profile fetch successful, user object updated:", updatedUser);
            setIsProfileLoaded(true);
            setLoading(false);
            
            // Handle enterprise email verification
            if (enterpriseNeedsVerification(updatedUser, firebaseUser)) {
              console.log('AuthContext: Enterprise user with unverified email detected');
              
              const evInProgress = sessionStorage.getItem('email_verification_in_progress') === 'true';
              if (evInProgress) {
                console.log('AuthContext: Email verification in progress, skipping redirect');
                return;
              }
              if (currentPath === '/verify-email') {
                console.log('AuthContext: Already on verification page, skipping redirect');
                return;
              }
              
              if (navigationRef.current) {
                const enterpriseData = await fetchEnterpriseData(updatedUser.enterpriseId);
                console.log('AuthContext: Redirecting enterprise user to verification page...');
                navigationRef.current('/verify-email', {
                  replace: true,
                  state: buildEnterpriseVerificationState(enterpriseData, updatedUser)
                });
                return;
              }
            } else if (isEnterpriseUser(updatedUser) && firebaseUser.emailVerified) {
              console.log('AuthContext: Enterprise user with verified email, ensuring proper access');
              const evCompletedRecently = sessionStorage.getItem('email_verification_completed_recently') === 'true';
              if (evCompletedRecently) {
                console.log('AuthContext: Email verification completed recently, avoiding redirect bounce');
                return;
              }
            }
            
            // Sync subscription data in background
            if (updatedUser && updatedUser.stripeCustomerId && !isEnterpriseUser(updatedUser)) {
              console.log("AuthContext: User has Stripe customer ID and is not enterprise, checking if sync is needed");
              try {
                const isFromCheckout = localStorage.getItem('stripe_checkout_initiated') === 'true' ||
                                      sessionStorage.getItem('in_stripe_checkout_flow') === 'true';
                
                import('../../services/subscriptionSyncService').then(({ isSyncAllowed }) => {
                  if (isFromCheckout || isSyncAllowed()) {
                    console.log("AuthContext: User sync allowed, syncing subscription data");
                    syncSubscriptionData((syncResult) => {
                      if (syncResult && syncResult.synced && syncResult.subscription) {
                        console.log("AuthContext: Subscription sync completed successfully:", syncResult);
                        
                        const oldPlanLevel = updatedUser?.planLevel;
                        const newPlanLevel = syncResult.subscription.planLevel;
                        
                        if (oldPlanLevel !== newPlanLevel) {
                          console.log(`AuthContext: Subscription plan changed from ${oldPlanLevel} to ${newPlanLevel}, updating user object`);
                          fetchUserProfile(firebaseUser, setUser, false).catch(err => {
                            console.error("AuthContext: Error refreshing user profile after subscription sync:", err);
                          });
                        }
                      } else if (syncResult && syncResult.cached) {
                        console.log("AuthContext: Subscription sync rate limited, using cached data");
                      }
                    }, false, updatedUser).catch(err => {
                      console.warn("AuthContext: Background subscription sync error (non-blocking):", err);
                    });
                  } else {
                    console.log("AuthContext: Subscription sync rate limited, skipping");
                  }
                }).catch(err => {
                  console.warn("AuthContext: Error importing sync service:", err);
                });
              } catch (syncError) {
                console.warn("AuthContext: Error starting subscription sync (non-blocking):", syncError);
              }
            }
            
            // Handle new signup plan level correction
            const isNewSignup = localStorage.getItem('just_signed_up') === 'true' || 
                               sessionStorage.getItem('coming_from_signup') === 'true' ||
                               newSignup;
            
            let userPlanLevel = updatedUser?.planLevel;
            
            if (isNewSignup && updatedUser) {
              console.log('AuthContext DEBUG: New signup detected, user data:', {
                userPlanLevel,
                stripeSubscriptionId: updatedUser.stripeSubscriptionId,
                stripeCustomerId: updatedUser.stripeCustomerId,
                hasStripeSubId: !!updatedUser.stripeSubscriptionId,
                hasStripeCustomerId: !!updatedUser.stripeCustomerId
              });
            }
            
            if (isNewSignup && updatedUser && userPlanLevel !== 'basic' && 
                !updatedUser.stripeSubscriptionId && !updatedUser.stripeCustomerId && 
                !updatedUser.subscriptionStatus && !updatedUser.planInterval) {
              console.log(`AuthContext: Correcting plan level for new signup from '${userPlanLevel}' to 'basic' (no subscription data found)`);
              userPlanLevel = 'basic';
              updatedUser.planLevel = 'basic';
              setUser(prevUser => ({
                ...prevUser,
                planLevel: 'basic'
              }));
            } else if (isNewSignup && updatedUser && userPlanLevel === 'premium' && 
                      (updatedUser.stripeSubscriptionId || updatedUser.stripeCustomerId)) {
              console.log(`AuthContext: New signup user has valid subscription, keeping premium plan level`);
              localStorage.removeItem('just_signed_up');
              sessionStorage.removeItem('coming_from_signup');
            } else if (isNewSignup && updatedUser && userPlanLevel === 'premium') {
              localStorage.removeItem('just_signed_up');
              sessionStorage.removeItem('coming_from_signup');
            }
            
            console.log(`AuthContext: User plan: ${userPlanLevel}, Current path: ${currentPath}`);

            // Skip redirects on success/cancel pages
            if (isSuccessOrCancelPage || isComingFromStripe) {
              console.log("AuthContext: On success/cancel page or coming from Stripe, skipping redirect");
              return;
            }
            
            // Redirect from login pages
            if (currentPath === '/login' || currentPath === '/signin') {
              if (userPlanLevel === 'premium' || updatedUser?.isEnterprise) {
                console.log("AuthContext: Premium/Enterprise user, redirecting to /app");
                if (navigationRef.current) {
                  navigationRef.current('/app', { replace: true });
                } else {
                  window.location.href = '/app';
                }
              } else {
                console.log("AuthContext: Basic user, redirecting to /pricing");
                if (navigationRef.current) {
                  navigationRef.current('/pricing', { replace: true });
                } else {
                  window.location.href = '/pricing';
                }
              }
            } else {
              console.log("AuthContext: User not on login page, skipping redirect (NavigationHandler will handle it)");
            }
          } catch (profileError) {
            console.error('AuthContext: Error fetching profile:', profileError);
            setLastError(profileError);
            
            // Handle 404 profile not found errors
            if (profileError.status === 404 || profileError.code === 'auth/profile-not-found') {
              console.log('AuthContext: Profile not found (404), checking context...');
              
              // Check for unverified enterprise user
              if (firebaseUser && firebaseUser.email && !firebaseUser.emailVerified) {
                console.log('AuthContext: Firebase user exists but email not verified, checking for enterprise domain');
                
                const enterprise = await checkEnterpriseDomain(firebaseUser.email);
                if (enterprise) {
                  console.log('AuthContext: User appears to be unverified enterprise user, redirecting to verification');
                  
                  const minimalUser = createMinimalEnterpriseUser(firebaseUser, enterprise);
                  setUser(minimalUser);
                  setIsProfileLoaded(true);
                  
                  if (navigationRef.current) {
                    navigationRef.current('/verify-email', {
                      replace: true,
                      state: buildEnterpriseVerificationState(enterprise, minimalUser)
                    });
                  }
                  return;
                }
              }
              
              // Check for recently created user
              const userCreationTime = firebaseUser?.metadata?.creationTime;
              const isRecentlyCreated = userCreationTime && 
                (Date.now() - new Date(userCreationTime).getTime()) < 10000;
              
              if (isRecentlyCreated) {
                console.log('AuthContext: User was recently created, profile might still be creating. Waiting before signing out...');
                
                const maxRetries = 3;
                let retryCount = 0;
                const originalUserId = firebaseUser.uid;
                
                const retryProfileFetch = async () => {
                  retryCount++;
                  console.log(`AuthContext: Retry ${retryCount}/${maxRetries} to fetch profile for user ${originalUserId}`);
                  
                  const currentFirebaseUser = auth.currentUser;
                  if (!currentFirebaseUser || currentFirebaseUser.uid !== originalUserId) {
                    console.log(`AuthContext: User changed during retry, aborting retry mechanism`);
                    return;
                  }
                  
                  try {
                    await new Promise(resolve => setTimeout(resolve, 2000));
                    
                    const currentUserAfterWait = auth.currentUser;
                    if (!currentUserAfterWait || currentUserAfterWait.uid !== originalUserId) {
                      console.log(`AuthContext: User changed during retry wait, aborting retry for ${originalUserId}`);
                      return;
                    }
                    
                    const retryResult = await fetchUserProfile(currentUserAfterWait, setUser, true);
                    
                    if (retryResult) {
                      console.log('AuthContext: Profile found on retry, setting user');
                      setUser(retryResult);
                      setIsProfileLoaded(true);
                      setLoading(false);
                      setIsTransitioning(false);
                      return;
                    }
                  } catch (retryError) {
                    console.log(`AuthContext: Retry ${retryCount} failed:`, retryError);
                  }
                  
                  if (retryCount < maxRetries) {
                    const currentUserBeforeNextRetry = auth.currentUser;
                    if (currentUserBeforeNextRetry && currentUserBeforeNextRetry.uid === originalUserId) {
                      setTimeout(retryProfileFetch, 1000);
                    } else {
                      console.log(`AuthContext: User changed before next retry, aborting retry for ${originalUserId}`);
                    }
                  } else {
                    const finalCurrentUser = auth.currentUser;
                    if (finalCurrentUser && finalCurrentUser.uid === originalUserId) {
                      const googleSignInStillInProgress = sessionStorage.getItem('googleSignInInProgress') === 'true';
                      if (googleSignInStillInProgress) {
                        console.log(`AuthContext: All retries exhausted, but Google sign-in still in progress - not signing out`);
                        return;
                      }
                      
                      console.log(`AuthContext: All retries exhausted for user ${originalUserId}, signing out`);
                      localStorage.setItem('refrral_auth_error', 'Account creation failed. Please try signing up again.');
                      try {
                        await firebaseSignOut(auth);
                        setUser(null);
                      } catch (signOutError) {
                        console.error('AuthContext: Error signing out after profile creation timeout:', signOutError);
                      }
                    }
                  }
                };
                
                setTimeout(retryProfileFetch, 1000);
                return;
              }
              
              // Try to repair orphaned user
              console.log('AuthContext: Profile not found for non-recent user, checking if orphaned...');
              
              try {
                const repairedUser = await detectAndRepairOrphanedUser(
                  firebaseUser, 
                  setUser,
                  {
                    onRepairStart: () => {
                      console.log("AuthContext: Starting orphaned user repair...");
                      setIsTransitioning(true);
                    },
                    onRepairComplete: (repairedData) => {
                      console.log("AuthContext: Orphaned user repair completed:", repairedData);
                      setIsProfileLoaded(true);
                      setLoading(false);
                      setIsTransitioning(false);
                    },
                    onRepairError: (repairError) => {
                      console.error("AuthContext: Orphaned user repair failed:", repairError);
                      setIsTransitioning(false);
                    }
                  }
                );
                
                if (repairedUser) {
                  console.log("AuthContext: Successfully repaired orphaned user, continuing with normal flow");
                  return;
                }
              } catch (repairError) {
                console.error("AuthContext: Error during orphaned user repair:", repairError);
              }
              
              // Normal 404 handling
              localStorage.setItem('refrral_auth_error', 'No account exists with this login. Please sign up for a new account first.');
              console.log('AuthContext: No profile found and not a recent signup, cleaning up auth state');
              try {
                await firebaseSignOut(auth);
                setUser(null);
              } catch (signOutError) {
                console.error('AuthContext: Error signing out after profile 404:', signOutError);
              }
            } else {
              localStorage.setItem('refrral_auth_error', profileError.message || 'Error fetching your profile');
            }
          }
        } else {
          // No firebase user
          console.log("AuthContext: No firebase user, clearing state");
          setUser(null);
          setDirectFirebaseUser(null);
          setIsProfileLoaded(false);
          setLoading(false);
        }

        // Check for stored Stripe checkout state
        if (isReturningFromStripeCheckout(firebaseUser)) {
          console.log('Detected returning from Stripe checkout flow');
          setIsStripeCheckoutInProgress(true);
        }
      } catch (error) {
        console.error('AuthContext: Error in auth state change handler:', error);
        setLoading(false);
        setIsProfileLoaded(false);
        setIsTransitioning(false);
        setLastError(error);
        if (error.status === 404 || error.code === 'auth/profile-not-found') {
          localStorage.setItem('refrral_auth_error', 'No account exists with this login. Please sign up for a new account first.');
        } else {
          localStorage.setItem('refrral_auth_error', error.message || 'Authentication error');
        }
      }
    });

    return () => unsubscribe();
  }, [newSignup]);

  // Event listener for API fallback user requests
  useEffect(() => {
    const handleGetAuthUser = (event) => {
      const effectiveUser = user || directFirebaseUser;
      const responseEvent = new CustomEvent('auth-user-response', {
        detail: { user: effectiveUser }
      });
      window.dispatchEvent(responseEvent);
    };

    window.addEventListener('get-auth-user', handleGetAuthUser);
    return () => window.removeEventListener('get-auth-user', handleGetAuthUser);
  }, [user, directFirebaseUser]);

  // Context value with all auth methods
  const value = {
    user,
    directFirebaseUser,
    loading,
    isProfileLoaded,
    isAuthenticating,
    isTransitioning,
    lastError,
    isAdmin: isUserAdmin(user),
    setUser,
    
    signIn: async (email, password, rememberMe = false) => {
      clearUserSessionData();
      setIsTransitioning(true);
      setIsProfileLoaded(false);
      setLastError(null);
      clearAllStoredAuthErrors();
      
      try {
        const result = await authSignIn(email, password, setUser, rememberMe);
        sessionStorage.setItem('justLoggedIn', 'true');
        console.log('AuthContext: Sign-in successful, set justLoggedIn flag');
        clearAllStoredAuthErrors();
        return result;
      } catch (error) {
        setLastError(error);
        setIsTransitioning(false);
        throw error;
      }
    },
    
    signUp: async (email, password, firstName, lastName, plan = 'basic', rememberMe = false, extraProfile = null) => {
      clearUserSessionData();
      setIsTransitioning(true);
      setIsProfileLoaded(false);
      setLastError(null);
      clearAllStoredAuthErrors();
      
      try {
        const result = await authSignUp(email, password, firstName, lastName, plan, setNewSignup, rememberMe, extraProfile);
        clearAllStoredAuthErrors();
        return result;
      } catch (error) {
        setLastError(error);
        setIsTransitioning(false);
        throw error;
      }
    },
    
    signOut: async () => {
      console.log("AuthContext: Starting sign out process");
      
      setIsProfileLoaded(false);
      setIsTransitioning(true);
      setLoading(true);
      setLastError(null);
      
      try {
        clearUserSessionData();
        console.log("AuthContext: Session data cleared");
        
        clearTokenCache();
        console.log("AuthContext: Token cache cleared");
        
        setUser(null);
        console.log("AuthContext: User state set to null");
        
        await new Promise(resolve => setTimeout(resolve, 50));
        await firebaseSignOut(auth);
        console.log("AuthContext: Firebase signOut completed");
        
        const googleSignInError = sessionStorage.getItem('google_signin_error') === 'true';
        const hasAuthError = localStorage.getItem('refrral_auth_error') !== null;
        const isOnSignInPage = window.location.pathname === '/login' || window.location.pathname === '/signin';
        
        if (googleSignInError || (hasAuthError && isOnSignInPage)) {
          console.log("AuthContext: Skipping redirect due to auth error on sign-in page");
          return;
        }
        
        if (navigationRef.current && window.location.pathname !== '/login' && window.location.pathname !== '/') {
          console.log("AuthContext: Using React Router navigation to home page");
          navigationRef.current('/', { replace: true });
        } else {
          console.log("AuthContext: Using window.location for navigation");
          window.location.href = '/';
        }
      } catch (error) {
        console.error("AuthContext: Error signing out:", error);
        setLastError(error);
        
        setUser(null);
        clearUserSessionData();
        console.log("AuthContext: Forced cleanup after signOut error");
        
        const googleSignInError = sessionStorage.getItem('google_signin_error') === 'true';
        const hasAuthError = localStorage.getItem('refrral_auth_error') !== null;
        const isOnSignInPage = window.location.pathname === '/login' || window.location.pathname === '/signin';
        
        if (googleSignInError || (hasAuthError && isOnSignInPage)) {
          return;
        }
        
        if (typeof window !== 'undefined') {
          window.location.href = '/';
        }
      } finally {
        setTimeout(() => {
          setIsTransitioning(false);
          setLoading(false);
          sessionStorage.removeItem('isSigningOut');
          console.log("AuthContext: Reset all transition flags after sign out");
        }, 1000);
      }
    },
    
    signInWithGoogle: async (firebaseUser, firstName, lastName) => {
      if (!firebaseUser) {
        console.log('AuthContext: No Firebase user provided to signInWithGoogle, possible cancellation');
        sessionStorage.removeItem('googleSignInInProgress');
        throw new Error('Google sign-in was cancelled or failed');
      }
      
      clearAllStoredAuthErrors();
      setIsTransitioning(true);
      setIsProfileLoaded(false);
      setLastError(null);
      
      try {
        console.log('AuthContext: Starting Google sign in process');
        
        if (!auth) {
          throw new Error('Authentication service unavailable. Please try again later.');
        }
        
        if (firebaseUser) {
          try {
            await firebaseUser.getIdToken(true);
            console.log('AuthContext: Successfully refreshed Firebase token after Google auth');
          } catch (tokenError) {
            console.error('AuthContext: Error refreshing token:', tokenError);
          }
        }
        
        const profile = await authSignInWithGoogle(
          firebaseUser,
          firstName,
          lastName,
          'basic',
          setUser,
          true,
          false,
          setNewSignup
        );
        
        const isPremiumOrPro = profile?.planLevel === 'premium' || profile?.isEnterprise;
        const redirectDestination = isPremiumOrPro ? '/app/' : '/pricing';
        
        console.log('AuthContext: Google sign-in successful, destination determined:', {
          planLevel: profile?.planLevel,
          isPremiumOrPro,
          redirectDestination,
          isNewSignup: newSignup
        });
        
        sessionStorage.setItem('justLoggedIn', 'true');
        
        if (newSignup) {
          console.log('AuthContext: New user created via Google sign-in');
          if (!localStorage.getItem('just_signed_up')) {
            localStorage.setItem('just_signed_up', 'true');
          }
          if (!sessionStorage.getItem('coming_from_signup')) {
            sessionStorage.setItem('coming_from_signup', 'true');
          }
          
          if (profile && profile.planLevel !== 'basic' && !profile.stripeSubscriptionId && !profile.stripeCustomerId) {
            console.log('AuthContext: Correcting plan level for new Google signup to basic');
            profile.planLevel = 'basic';
            setUser({...profile, planLevel: 'basic'});
          } else if (profile && profile.planLevel === 'premium' && (profile.stripeSubscriptionId || profile.stripeCustomerId)) {
            localStorage.removeItem('just_signed_up');
            sessionStorage.removeItem('coming_from_signup');
          }
        }
        
        setIsProfileLoaded(true);
        setLastError(null);
        clearAllStoredAuthErrors();
        sessionStorage.removeItem('googleSignInInProgress');
        
        setTimeout(() => {
          if (auth.currentUser) {
            console.log('AuthContext: Triggering profile processing after Google sign-in');
            auth.currentUser.getIdToken(true).catch(console.error);
          }
        }, 500);
        
        return profile;
      } catch (error) {
        console.error('AuthContext: Error in signInWithGoogle:', error);
        setLastError(error);
        sessionStorage.removeItem('googleSignInInProgress');
        
        if (auth.currentUser) {
          setTimeout(() => {
            auth.currentUser.getIdToken(true).catch(console.error);
          }, 100);
        }
        
        if (error.message) {
          localStorage.setItem('refrral_auth_error', error.message);
        }
        
        setIsTransitioning(false);
        throw error;
      }
    },
    
    signUpWithGoogle: async (user, firstName, lastName) => {
      clearAllStoredAuthErrors();
      setLoading(true);
      
      try {
        console.log('AuthContext: Starting Google sign up process');
        
        await setPersistence(auth, browserLocalPersistence);
        
        const profile = await authSignUpWithGoogle(
          user,
          firstName,
          lastName,
          'basic',
          setNewSignup,
          setUser,
          true
        );
        
        setLoading(false);
        setLastError(null);
        clearAllStoredAuthErrors();
        
        storeAuthBackup(user, { firstName, lastName });
        localStorage.setItem('just_signed_up', 'true');
        console.log('Auth backup saved after Google signup');
        
        return profile;
      } catch (error) {
        console.error('AuthContext: Error in signUpWithGoogle:', error);
        setLastError(error);
        setLoading(false);
        
        if (error.message) {
          localStorage.setItem('refrral_auth_error', error.message);
        }
        
        throw error;
      }
    },
    
    deleteCurrentUserAccount: async (exitSurveyData = null) => {
      setIsTransitioning(true);
      setLoading(true);
      setLastError(null);
      setIsProfileLoaded(false);
      
      if (!auth.currentUser) {
        console.warn("Attempted to delete account, but no user is signed in.");
        setIsTransitioning(false);
        setLoading(false);
        return;
      }

      try {
        const token = await auth.currentUser.getIdToken();
        console.log("Attempting to delete user account via API...");

        const requestBody = exitSurveyData ? { exitSurvey: exitSurveyData } : {};

        await axios.delete(`${SERVER_URL}/auth/delete-account`, {
          headers: { Authorization: `Bearer ${token}` },
          data: requestBody
        });

        console.log("Account deletion successful via API.");
        
        setUser(null);
        clearUserSessionData();
        
        try {
          await new Promise(resolve => setTimeout(resolve, 50));
          await firebaseSignOut(auth);
          console.log("Firebase sign out completed after account deletion");
        } catch (signOutError) {
          console.error("Error signing out after account deletion:", signOutError);
        }
        
        if (navigationRef.current) {
          navigationRef.current('/', { replace: true });
        } else {
          window.location.href = '/';
        }
        
        return { success: true, message: "Your account has been successfully deleted." };
      } catch (error) {
        console.error("Error deleting account:", error.response?.data || error.message);
        const deleteError = new Error("Failed to delete account. Please try again.");
        if (error.response?.data?.message) {
          deleteError.message = error.response.data.message;
        } else if (error.code) {
          if (error.code === 'auth/requires-recent-login') {
            deleteError.message = "Please sign in again to delete your account.";
          }
          deleteError.code = error.code;
        }
        setLastError(deleteError);
        setIsTransitioning(false);
        setLoading(false);
        throw deleteError;
      } finally {
        if (lastError) {
          setTimeout(() => {
            setIsTransitioning(false);
            setLoading(false);
          }, 300);
        }
      }
    },
    
    completeNewSignup: () => completeNewSignup(setNewSignup, setUser),
    newSignup,
    setUserProfile: (profileData) => setUserProfile(profileData, setUser),
    fetchUserProfile: async (firebaseUser, forceRefresh = false) => {
      return fetchUserProfile(firebaseUser, setUser, forceRefresh);
    },
    resetTransitioning: () => {
      console.log("AuthContext: Resetting auth transitioning state");
      sessionStorage.removeItem('isTransitioning');
      setIsTransitioning(false);
    },
    clearLastError: () => {
      console.log("Clearing auth last error");
      setLastError(null);
    },
    setIsTransitioning,
    isStripeCheckoutInProgress,
    initiateStripeCheckout: (sessionId) => {
      const success = stripeInitiateCheckout(sessionId, user, directFirebaseUser);
      if (success) {
        setIsStripeCheckoutInProgress(true);
      }
      return success;
    },
    finalizeStripeCheckout: (success = false, sessionId = null) => {
      const result = stripeFinalizeCheckout(success, sessionId);
      setIsStripeCheckoutInProgress(false);
      return result;
    },
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

